import { useMemo } from 'react';
import type { Module, ModulePitfall } from '../types';

/**
 * Pitfalls on a module — known ways students go wrong on the topic, checked by
 * the analyser on every attempt at a question here or below. Admin only; the
 * word "pitfall" appears nowhere a teacher or student looks.
 *
 * Remove deletes a pitfall; Retire keeps its text but stops the analyser being
 * shown it. The server assigns ids and audit fields.
 * See docs/FeatureDesigns/diagnosis/topic-pitfalls-target.md.
 */

/** Mirrors the backend's MAX_PITFALLS_PER_CALL — past this, the most distant
 *  inherited ones do not reach the analyser. */
const MAX_PITFALLS_PER_CALL = 8;

interface Props {
  pitfalls: ModulePitfall[] | undefined;
  onChange: (pitfalls: ModulePitfall[]) => void;
  /** The module being edited (undefined while creating) and its parent, to
   *  show what is inherited. */
  moduleId?: string;
  parentModuleId?: string;
  /** Every module loaded on the page — ancestors are looked up here. */
  allModules: Module[];
}

export function ModulePitfallsEditor({ pitfalls, onChange, moduleId, parentModuleId, allModules }: Props) {
  const list = pitfalls ?? [];

  // Ancestors' live pitfalls, nearest first, as the analyser will get them.
  const inherited = useMemo(() => {
    const byId = new Map(allModules.map(m => [m.id, m]));
    const out: { moduleName: string; pitfall: ModulePitfall }[] = [];
    const visited = new Set<string>(moduleId ? [moduleId] : []);
    let id = parentModuleId;
    let missingAncestor = false;
    for (let hop = 0; id && hop < 10 && !visited.has(id); hop++) {
      visited.add(id);
      const mod = byId.get(id);
      if (!mod) { missingAncestor = true; break; }
      for (const p of mod.pitfalls ?? []) if (!p.retired) out.push({ moduleName: mod.displayName, pitfall: p });
      id = mod.parentModuleId;
    }
    return { rows: out, missingAncestor };
  }, [allModules, moduleId, parentModuleId]);

  const liveOwn = list.filter(p => !p.retired).length;
  const total = liveOwn + inherited.rows.length;

  const update = (i: number, patch: Partial<ModulePitfall>) =>
    onChange(list.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  return (
    <div className="form-group">
      <label>Pitfalls</label>
      <p className="form-help">
        Known mistakes on this topic. The analyser checks every attempt at a question in this module
        or below it for them, and reports one only when a line of the student's working commits it.
        Write the mistake and what in the working shows it, one sentence each. Never a remedy — that
        is a learning moment. Analyser only: not shown in hints, chat or voice.
      </p>

      {inherited.rows.length > 0 && (
        <div className="pitfalls-inherited">
          <div className="pitfalls-subhead">Inherited from parent modules (edit them there)</div>
          {inherited.rows.map(({ moduleName, pitfall }) => (
            <div key={pitfall.id} className="pitfall-inherited-row">
              <span className="pitfall-source">{moduleName}</span>
              <span>{pitfall.pitfall} <em>Check:</em> {pitfall.check}</span>
            </div>
          ))}
        </div>
      )}
      {inherited.missingAncestor && (
        <p className="form-help">Some parent modules are not loaded under the current filter — clear the grade filter to see everything inherited.</p>
      )}

      <div className="instructions-editor">
        {list.map((p, i) => (
          <div key={p.id ?? `new-${i}`} className={`instruction-row pitfall-row${p.retired ? ' pitfall-retired' : ''}`}>
            <div className="pitfall-fields">
              <textarea
                value={p.pitfall}
                className="form-textarea instruction-value"
                placeholder="The mistake — e.g. A constant-acceleration formula used under a force that varies with distance."
                rows={2}
                disabled={p.retired}
                onChange={(e) => update(i, { pitfall: e.target.value })}
              />
              <textarea
                value={p.check}
                className="form-textarea instruction-value"
                placeholder="The check — e.g. A line substitutes an acceleration into v² = u² + 2as while the force depends on position."
                rows={2}
                disabled={p.retired}
                onChange={(e) => update(i, { check: e.target.value })}
              />
              {p.addedBy && <span className="pitfall-meta">added by {p.addedBy}{p.retired ? ' · retired' : ''}</span>}
            </div>
            {p.id && (
              <button
                type="button"
                className="btn-pitfall-retire"
                title={p.retired ? 'Bring this pitfall back' : 'Stop checking for this pitfall, keeping its text'}
                onClick={() => update(i, { retired: !p.retired })}
              >
                {p.retired ? 'Restore' : 'Retire'}
              </button>
            )}
            <button
              type="button"
              className="btn-remove-instruction"
              title="Remove this pitfall"
              onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-add-instruction"
          onClick={() => onChange([...list, { pitfall: '', check: '' }])}
        >
          + Add pitfall
        </button>
        {total > MAX_PITFALLS_PER_CALL && (
          <p className="form-help">
            {total} live pitfalls including inherited ones; the analyser gets the {MAX_PITFALLS_PER_CALL} nearest,
            so the most distant inherited ones are left out for questions here.
          </p>
        )}
      </div>
    </div>
  );
}
